import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

const CartSidebar = () => {
  const {
    items,
    isOpen,
    setCartOpen,
    updateQuantity,
    removeFromCart,
    getCartTotal,
    getCartItemsCount,
  } = useCart();

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isOpen]);

  const cartTotal = getCartTotal();
  const itemsCount = getCartItemsCount();

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={() => setCartOpen(false)}
      />

      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-[90%] sm:w-full max-w-[400px] bg-white shadow-2xl z-50 transform transition-transform duration-300">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
            <h2 className="text-2xl sm:text-3xl font-light text-gray-900 tracking-tight">Shopping Cart ({itemsCount})</h2>
            <button
              onClick={() => setCartOpen(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <ShoppingBag className="w-12 h-12 sm:w-16 sm:h-16 mb-4 text-gray-300" />
                <p className="text-xl sm:text-2xl font-medium mb-2 text-gray-900">Your cart is empty</p>
                <p className="text-base text-center px-4">Add some beautiful flowers to get started!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="group relative flex items-center space-x-4 bg-gradient-to-br from-white to-emerald-50/30 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-4 border border-emerald-100/50 dark:border-emerald-900/30 shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-light text-gray-900 dark:text-white text-lg sm:text-xl truncate mb-1">
                        {item.name}
                      </h3>


                      {/* Quantity Controls */}
                      <div className="flex items-center space-x-3 mt-4">
                        <div className="flex items-center bg-white/80 dark:bg-gray-800 backdrop-blur-sm rounded-full border border-emerald-100 shadow-sm p-1">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-emerald-50 text-emerald-600 transition-colors"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="text-base font-normal w-8 text-center text-gray-800 dark:text-white">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-emerald-50 text-emerald-600 transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-end justify-between self-stretch py-0.5">
                      <div className="text-right">
                        <p className="text-gray-900 dark:text-white font-medium text-xl">
                          {(item.price * item.quantity).toLocaleString()} ETB
                        </p>
                      </div>

                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="group/btn relative flex items-center justify-center w-10 h-10 rounded-full bg-rose-50 hover:bg-rose-100 text-rose-400 hover:text-rose-600 transition-all duration-300 shadow-sm"
                        title="Remove item"
                      >
                        <X className="w-5 h-5" />
                        <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 scale-0 group-hover/btn:scale-100 transition-transform text-[10px] font-bold text-rose-500 uppercase tracking-widest pb-2 whitespace-nowrap">
                          Remove
                        </span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-xl font-normal text-gray-900 dark:text-white">Total:</span>
                <span className="text-3xl font-normal text-emerald-600">
                  {cartTotal.toLocaleString()} ETB
                </span>
              </div>

              <div className="space-y-2">
                <Link
                  to="/checkout"
                  onClick={() => setCartOpen(false)}
                  className="w-full bg-emerald-600 text-white py-4 px-6 rounded-lg font-normal hover:bg-emerald-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-lg tracking-wide"
                >
                  Checkout
                </Link>
                <Link
                  to="/shop"
                  onClick={() => setCartOpen(false)}
                  className="w-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 py-4 px-4 rounded-lg font-bold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-center block text-lg"
                >
                  Continue Shopping
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CartSidebar;

